import { has, type, map } from 'ramda'

// PropMapper allows object instantiation if rawModel is not given
// if rawModel is given however, it will do certain validations for the fields which if they fail are logged to the console as errors
export default class PropMapper {
  constructor({ name, instance, rawModel, mockLog }) {
    this.name = name
    this.instance = instance
    this.rawModel = rawModel
    this.log = mockLog || console.error
  }

  propOr({ prop, defaultValue, expectedType, transform, options }) {
    if (!this.rawModel) this.instance[prop] = defaultValue
    else {
      if (!has(prop)(this.rawModel)) {
        this.log(`Missing prop ${prop} while mapping model ${this.name}`)
        this.instance[prop] = defaultValue
      } else {
        const rawValue = this.rawModel[prop]
        if (expectedType && rawValue !== null && type(rawValue) !== expectedType)
          this.log(
            `Expected ${prop} to be of type ${expectedType} while mapping model ${
              this.name
            }, got ${rawValue} of type ${type(rawValue)}`,
          )
        if (options && !options.includes(rawValue))
          this.log(
            `Expected ${prop} to be of one of [${options}] while mapping model ${this.name}, but was ${rawValue}`,
          )

        if (rawValue && transform) this.instance[prop] = transform(rawValue)
        else this.instance[prop] = rawValue
      }
    }
    return this
  }

  mapOptionalProp({ prop, transform, defaultValue }) {
    if (this.rawModel && this.rawModel[prop]) {
      if (transform) this.instance[prop] = transform(this.rawModel[prop])
      else this.instance[prop] = this.rawModel[prop]
    }
    return this
  }

  mapString(prop) {
    return this.propOr({ prop, defaultValue: null })
  }

  mapBool(prop) {
    return this.propOr({ prop, defaultValue: false, expectedType: 'Boolean' })
  }

  mapNumber(prop) {
    return this.propOr({ prop, defaultValue: 0, expectedType: 'Number' })
  }

  mapArray(prop, mapper) {
    return this.propOr({
      prop,
      defaultValue: [],
      expectedType: 'Array',
      transform: mapper ? map(mapper) : null,
    })
  }

  mapObject(prop, mapper) {
    return this.propOr({ prop, defaultValue: null, transform: mapper })
  }

  mapOptionalObject(prop, mapper) {
    return this.mapOptionalProp({ prop, transform: mapper })
  }

  mapOneOf(prop, options) {
    return this.propOr({ prop, defaultValue: options[0], expectedType: 'String', options })
  }
}
